import { useCallback, useContext, useMemo } from 'react';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import { useDeleteReportMutation } from '@/modules/common/queries/reports.ts';
import { dateToPrint } from '@/modules/common/utils/date-format.ts';
import { IssuerAddAnnouncementModal } from '@/modules/issuer-dashboard/components/modals/IssuerAddAnnouncementModal.tsx';
import { DisclosureListItem, ModalProviderContext, Spinner, TooltipNew } from 'ui';
import { DownloadIcon, SendGreyIcon, TrashGrayIcon, TrashIcon } from 'ui/icons';
import { fixNameEnum } from 'ui/utils';
import { RegisterClass, ReportFormat, ReportWithUrlResponse } from 'api';

export interface ReportCardProps {
  report: ReportWithUrlResponse;
  canDelete?: boolean;
  sendingRegisterClass?: RegisterClass;
}

export const ReportCard = ({ report, canDelete = true, sendingRegisterClass }: ReportCardProps) => {
  const {
    id,
    generated_at,
    status,
    filename,
    type,
    entity_id,
    format,
    file_storage_item_url,
    created_by,
    register_id,
    classes,
  } = report;
  const { openModal } = useContext(ModalProviderContext);
  const isAllowedDeletion = !!created_by;
  const getRegisterTypeStrings = useRegisterTypeStrings();
  const { mutateAsync: deleteReport, isLoading: isReportDeletion } = useDeleteReportMutation();

  const handleReportDelete = async () => {
    deleteReport(id);
  };

  const handleOpenCommunicationModal = useCallback(() => {
    openModal(IssuerAddAnnouncementModal, {
      reports: [
        {
          label: filename,
          value: { id, name: filename, type: type, entity_id, classes },
        },
      ],
      registerClass: sendingRegisterClass
        ? {
            value: sendingRegisterClass.id,
            label: sendingRegisterClass.class_name,
          }
        : undefined,
    });
  }, [classes, entity_id, filename, id, openModal, sendingRegisterClass, type]);

  const rightContent =
    status === 'generating' ? (
      <div className="flex items-center bg-gray-200 rounded-xl px-3 py-1">
        <Spinner size="sm" className="grayscale" />
        <span className="text-xs font-medium">Generating</span>
      </div>
    ) : (
      <div className="flex gap-2 select-none">
        <button onClick={handleOpenCommunicationModal}>
          <img
            src={SendGreyIcon}
            alt="send"
            className="hover:opacity-70 cursor-pointer min-w-5 w-5 h-5 min-h-5"
          />
        </button>
        <a
          href={file_storage_item_url}
          rel="noreferrer"
          target={format === ReportFormat.Pdf ? '_blank' : '_self'}
        >
          <img
            src={DownloadIcon}
            className="hover:opacity-70 cursor-pointer min-w-5 w-5 h-5 min-h-5"
            alt="download"
          />
        </a>
        {canDelete && (
          <TooltipNew
            text="Unable to delete a system-generated report"
            disabled={isAllowedDeletion}
          >
            <button onClick={handleReportDelete} disabled={isReportDeletion || !isAllowedDeletion}>
              {isReportDeletion ? (
                <Spinner size="xs" className="m-0" />
              ) : (
                <img
                  src={isAllowedDeletion ? TrashIcon : TrashGrayIcon}
                  className="hover:opacity-70 min-w-5 w-5 h-5 min-h-5"
                  alt="delete"
                />
              )}
            </button>
          </TooltipNew>
        )}
      </div>
    );

  const leftSubduedContentSet = useMemo(() => {
    const res = [
      fixNameEnum(type)
        .replace(
          'Distribution',
          getRegisterTypeStrings('dividend', { capitalize: true, registerId: register_id }),
        )
        .replace(
          'Dividend',
          getRegisterTypeStrings('dividend', { capitalize: true, registerId: register_id }),
        ),
      format.toUpperCase(),
    ];

    if (generated_at) {
      res.unshift(dateToPrint(generated_at));
    }

    return res;
  }, [format, generated_at, getRegisterTypeStrings, register_id, type]);

  return (
    <DisclosureListItem
      leftMainString={filename
        ?.replace(
          'Distribution',
          getRegisterTypeStrings('dividend', { capitalize: true, registerId: register_id }),
        )
        .replace(
          'Dividend',
          getRegisterTypeStrings('dividend', { capitalize: true, registerId: register_id }),
        )}
      leftSubduedContentSet={leftSubduedContentSet}
      rightContent={rightContent}
    />
  );
};
